import React, { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { StopList, StopRow } from '@mytours/components'
import { CollectionItem, PlaylistItem } from 'mytours-read-common'

import ErrorMessageHandler from 'mobile_web/modules/common/components/error-message-handler'
import ImagePlaceholder from '../../common/components/placeholders/Image'
import TextPlaceholder from '../../common/components/placeholders/Text'
import AudioPlayerSquare from '../../audio/components/v2/AudioPlayerSquare'
import StopScreen from './StopScreen'
import { getDirMode } from '../../platform'
import { ImageSizePreset } from '../../../common_lib/types'
import { generateImageSrcSet, generateImageUrl } from '../../../common_lib/utils'
import { stopActiveAudio, stopLeadingAudio } from '../functions'
import strings from '../../../strings'

interface Props {
	collectionItems?: CollectionItem[]
	collectionId: number
	isLoadingStops: boolean
	errorLoadingStops?: Error
	// The stop the reader has open, or null when every row is collapsed. The list is controlled: it
	// holds no open state of its own, so the page above it stays the single source of truth.
	openStopId: string | null
	onOpenStopChange: (stopId: string | null) => void
}

const SKELETON_ROWS = 3

// How long to keep following the page while it settles under an opening row. Long enough for the images
// above the list to load on a slow connection, short enough that a row which simply cannot reach the top
// stops being chased.
const SCROLL_SETTLE_MS = 2000

// How many frames the row has to hold still before the page is believed to have settled. One frame is
// not enough: a row is momentarily still in the middle of the accordion's open/close animation, and
// stopping there leaves it wherever the rest of the animation puts it — which for a row above the one
// being closed is somewhere off the top of the screen.
const STILL_FRAMES = 20

// What goes over a stop's thumbnail, and this layout's only player while a row is collapsed — there is
// no sticky bar behind it. The row positions this and expands itself when it is pressed; the player owns
// playback and its own name, which flips between "Play …" and "Pause …" as it goes.
//
// One expression, over current state and nothing else:
//
//   pill = the track this stop has going, or else the audio this stop leads with
//
// so the stop the reader is listening to always carries a pill, wherever inside it that track sits, and
// reverts once that track is stopped or spent — to a play pill if the stop leads with audio, and to a
// plain thumbnail if it does not. Nothing here remembers that a stop was playing, so nothing has to
// unwind it.
const stopOverlay = (
	stop: CollectionItem,
	collectionId: number,
	rowName: string,
	current?: PlaylistItem,
): React.ReactNode => {
	const track = stopActiveAudio(stop, current) ?? stopLeadingAudio(stop)
	if (!track) {
		return undefined
	}

	return (
		<AudioPlayerSquare
			audioPreview={track.audio}
			container={track.container}
			collectionId={collectionId}
			screen={stop.screen}
			name={rowName}
		/>
	)
}

const stopThumbnail = (stop: CollectionItem): { src?: string; srcSet?: string } => {
	const image = stop.cover_image_grid || stop.cover_image
	const focalPoint = image?.file?.focal_point
	const crop = focalPoint && (focalPoint[0] !== 0.5 || focalPoint[1] !== 0.5)

	if (!image?.file) {
		return {}
	}

	const options = { imageSizePreset: ImageSizePreset.Thumbnail, crop, focalPoint }

	return { src: generateImageUrl(image.file, options), srcSet: generateImageSrcSet(image.file, options) }
}

const CollectionTourStopList: React.FC<Props> = ({
	collectionItems,
	collectionId,
	isLoadingStops,
	errorLoadingStops,
	openStopId,
	onOpenStopChange,
}) => {
	// The current track, whatever its state — which stop owns it, and whether it still counts as going,
	// are `stopActiveAudio`'s to answer. The entry comes straight off the store, so the pill it feeds is
	// keyed on the same track the transport inside the panel registered.
	const currentTrack = useSelector(
		(state: { audioplayer: { playlist: Map<string, PlaylistItem>; currentPlaylistId: string } }) =>
			state.audioplayer.playlist.get(state.audioplayer.currentPlaylistId),
	)

	const listRef = useRef<HTMLDivElement>(null)

	// Bring the open stop to the top of the page, whether it was opened by a press or named by the URL.
	//
	// Both need it. A shared link, a QR scan or a keypad entry lands with a row already open somewhere
	// down a page nobody has scrolled. And a row the reader presses grows downward as it opens, which
	// leaves them looking at the middle of a panel whose heading has gone off the top.
	//
	// Asking once is not enough, which is the whole reason this is a loop. On arrival the page is
	// usually not scrollable yet — the hero image above the list has not loaded, the content is shorter
	// than the window, and `scrollIntoView` has nowhere to go and does nothing. And opening one row
	// closes another, so the ground keeps moving while the two animations run.
	//
	// So it asks again every frame until the row has held still for a while, which covers both: it
	// follows the images in, it follows the animation out, and it settles for wherever the last row can
	// reach when there is not enough page below it to put the row at the top.
	//
	// Where the row comes to rest is `scroll-margin-block-start`'s to say — the header floats over the
	// page, and a row that landed flush would sit under it and read as the wrong row being open.
	useEffect(() => {
		if (!openStopId) {
			return
		}

		let frame = 0
		let stopped = false
		let stillFrames = 0
		let previousTop = Number.NaN
		const giveUpAt = Date.now() + SCROLL_SETTLE_MS

		// The reader moving the page themselves outranks this.
		const abort = () => {
			stopped = true
		}

		const bring = () => {
			const row = listRef.current?.querySelector(`[data-stop-id="${openStopId}"]`)
			if (stopped || !row) {
				return
			}

			row.scrollIntoView({ block: 'start' })

			const top = row.getBoundingClientRect().top
			stillFrames = top === previousTop ? stillFrames + 1 : 0
			previousTop = top

			if (stillFrames < STILL_FRAMES && Date.now() < giveUpAt) {
				frame = requestAnimationFrame(bring)
			}
		}

		frame = requestAnimationFrame(bring)
		addEventListener('wheel', abort, { passive: true })
		addEventListener('touchstart', abort, { passive: true })

		return () => {
			stopped = true
			cancelAnimationFrame(frame)
			removeEventListener('wheel', abort)
			removeEventListener('touchstart', abort)
		}
	}, [openStopId, collectionItems])

	if (errorLoadingStops) {
		return <ErrorMessageHandler error={errorLoadingStops} />
	}

	if (isLoadingStops || !collectionItems) {
		return (
			<div className="tour-stop-list-skeleton">
				{Array.from({ length: SKELETON_ROWS }, (_row, index) => (
					<div className="stop-skeleton" key={index}>
						<ImagePlaceholder className="stop-skeleton-thumbnail" />
						<TextPlaceholder height={40} viewBox="0 0 1200 40" />
					</div>
				))}
			</div>
		)
	}

	return (
		// Base UI stamps its own `dir` from a context that defaults to 'ltr', so an RTL page has to be
		// told again here or every logical property in the row's CSS resolves the wrong way.
		<StopList
			ref={listRef}
			className="tour-stop-list"
			dir={getDirMode()}
			value={openStopId}
			onValueChange={onOpenStopChange}
			aria-label={strings.stops}
		>
			{collectionItems.map(stop => {
				// The authored stop number, which matches physical signage and keypad entry. It is optional,
				// so a stop without one is named by its title alone rather than by its position in the list.
				const stopLabel =
					stop.item_number == null
						? undefined
						: (strings.formatString(strings.stop_number, { number: stop.item_number }) as string)
				const rowName = stopLabel ? `${stopLabel}: ${stop.title}` : stop.title

				return (
					<StopRow
						key={stop.id}
						// The item id, not the collection item's: it is what QR scans, keypad entry and stop
						// routes already address a stop by.
						value={String(stop.item_id)}
						// What the arrival scroll finds the row by. The accordion keys itself on `value` but puts
						// nothing in the DOM that names it.
						data-stop-id={String(stop.item_id)}
						title={stop.title}
						stopLabel={stopLabel}
						headingLevel={2}
						overlay={stopOverlay(stop, collectionId, rowName, currentTrack)}
						{...stopThumbnail(stop)}
					>
						{/* Only the open row draws its screen, and stated here rather than left to the row:
						    a collapsed panel keeps its children out of the DOM today, but that is a
						    `keepMounted` away from changing, and a page of stops would then be a page of
						    players each fetching its own metadata range to draw a length nobody can see.
						    Unmounting one does not stop its sound — the controller owns that. */}
						{openStopId === String(stop.item_id) && (
							<StopScreen stop={stop} collectionId={collectionId} name={rowName} />
						)}
					</StopRow>
				)
			})}
		</StopList>
	)
}

export default CollectionTourStopList
